import { useEffect, useState } from "react";

export type FinanceDialogBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type SidebarRect = Pick<DOMRect, "left" | "right" | "width"> | null;

export function calculateFinanceDialogBounds(
  viewportWidth: number,
  viewportHeight: number,
  sidebarRect: SidebarRect,
  maxWidth = Number.POSITIVE_INFINITY
): FinanceDialogBounds {
  const sidebarVisible = Boolean(
    sidebarRect &&
    sidebarRect.width > 0 &&
    sidebarRect.right > 24 &&
    sidebarRect.left >= -4
  );
  const left =
    viewportWidth >= 768 && sidebarVisible
      ? Math.round((sidebarRect?.right ?? 0) + 12)
      : 12;

  return {
    left,
    top: 12,
    width: Math.max(0, Math.min(maxWidth, viewportWidth - left - 24)),
    height: Math.max(0, viewportHeight - 24),
  };
}

function getInitialBounds(maxWidth: number): FinanceDialogBounds {
  if (typeof window === "undefined") {
    return { left: 12, top: 12, width: 0, height: 0 };
  }

  const sidebar = document.querySelector<HTMLElement>(
    '[data-slot="sidebar-container"]'
  );
  return calculateFinanceDialogBounds(
    window.innerWidth,
    window.innerHeight,
    sidebar?.getBoundingClientRect() ?? null,
    maxWidth
  );
}

/** Keep large finance detail dialogs inside the visible area and clear of the sidebar. */
export function useFinanceDialogBounds(
  open: boolean,
  maxWidth = Number.POSITIVE_INFINITY
) {
  const [bounds, setBounds] = useState(() => getInitialBounds(maxWidth));

  useEffect(() => {
    if (!open) return;

    const sidebar = document.querySelector<HTMLElement>(
      '[data-slot="sidebar-container"]'
    );
    const updateBounds = () => {
      const next = calculateFinanceDialogBounds(
        window.innerWidth,
        window.innerHeight,
        sidebar?.getBoundingClientRect() ?? null,
        maxWidth
      );
      setBounds(current =>
        current.left === next.left &&
        current.top === next.top &&
        current.width === next.width &&
        current.height === next.height
          ? current
          : next
      );
    };

    updateBounds();
    window.addEventListener("resize", updateBounds);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener("resize", updateBounds);

    const resizeObserver = sidebar ? new ResizeObserver(updateBounds) : null;
    if (sidebar) resizeObserver?.observe(sidebar);
    const mutationObserver = sidebar
      ? new MutationObserver(updateBounds)
      : null;
    if (sidebar)
      mutationObserver?.observe(sidebar, {
        attributes: true,
        attributeFilter: ["data-state", "data-collapsible"],
      });

    return () => {
      window.removeEventListener("resize", updateBounds);
      visualViewport?.removeEventListener("resize", updateBounds);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [open, maxWidth]);

  return bounds;
}
